/**
 * The focus pipeline: stages 4-9 wired together.
 *
 * Host-agnostic on purpose. The pipeline never touches a camera, a canvas or a file — the
 * host hands it landmarks and pixel metrics for one frame, and it returns the observation,
 * the direction sample, the state evaluation and any completed events. That boundary is what
 * lets an entire workday of behaviour be simulated in a unit test in milliseconds, and what
 * lets the same logic run in a browser pilot, an Electron agent, or a replay harness without
 * modification.
 *
 * Rate separation (§26) is the caller's job, assisted by `AdaptiveRateController`: capture
 * runs at the camera's rate, inference at a lower adaptive rate, and events emerge only on
 * transitions.
 */

import { DirectionClassifier, thresholdsFromPolicy, type EffectiveThresholds } from './attention/classifier.js';
import { AttentionEventDetector } from './attention/events.js';
import { AttentionStateEngine, type StateEvaluation } from './attention/stateEngine.js';
import { EventAccumulator, StateAccumulator } from './attention/stats.js';
import {
  effectiveThresholdsFor,
  profileStalenessNote,
  type CalibrationProfile,
} from './calibration/profile.js';
import type { Policy } from './config.js';
import { SystemClock, type Clock } from './clock.js';
import {
  combineConfidence,
  type AttentionEvent,
  type AttentionEventStats,
  type DirectionSample,
  type FaceLandmarks,
  type FaceObservation,
  type FrameQuality,
} from './models.js';
import { PnPHeadPoseEstimator, type HeadPoseEstimator } from './vision/headPose.js';
import { PoseSmoother } from './vision/smoothing.js';
import {
  assessFace,
  assessFrame,
  qualityConfidenceFactor,
  unavailableQuality,
  withFlag,
  type FrameMetrics,
} from './vision/quality.js';

export interface FrameInput {
  readonly monotonicTs: number;
  readonly timestampUtc: Date;
  /** Null when the detector found no face in this frame. */
  readonly landmarks: FaceLandmarks | null;
  /** Null when no frame was available at all (camera unplugged, permission revoked). */
  readonly metrics: FrameMetrics | null;
}

export interface PipelineTick {
  readonly observation: FaceObservation;
  readonly sample: DirectionSample;
  readonly evaluation: StateEvaluation;
  readonly events: readonly AttentionEvent[];
}

export interface PipelineOptions {
  clock?: Clock;
  estimator?: HeadPoseEstimator;
  calibration?: CalibrationProfile | null;
  sessionId?: string;
  agentVersion?: string;
}

export interface PipelineSummary {
  readonly sessionId: string;
  readonly configVersion: string;
  readonly modelVersion: string;
  readonly eventStats: AttentionEventStats;
  readonly minorShiftCount: number;
  readonly ignoredShiftCount: number;
  readonly suppressedEventCount: number;
  readonly stateTotals: Record<string, unknown>;
  readonly framesProcessed: number;
  readonly framesWithFace: number;
  readonly framesUnsolved: number;
  readonly notes: readonly string[];
}

export class FocusPipeline {
  private readonly policy: Policy;
  private readonly clock: Clock;
  private readonly estimator: HeadPoseEstimator;
  private readonly smoother: PoseSmoother;
  private readonly classifier: DirectionClassifier;
  private readonly engine: AttentionStateEngine;
  private readonly detector: AttentionEventDetector;
  private readonly eventStats = new EventAccumulator();
  private readonly stateStats: StateAccumulator;

  private readonly sessionId: string;
  private readonly notes: string[] = [];

  private framesProcessed = 0;
  private framesWithFace = 0;
  private framesUnsolved = 0;
  private firstTs: number | null = null;
  private lastTs: number | null = null;

  constructor(policy: Policy, options: PipelineOptions = {}) {
    this.policy = policy;
    this.clock = options.clock ?? new SystemClock();
    this.estimator =
      options.estimator ??
      new PnPHeadPoseEstimator({
        maxReprojectionErrorPx: policy.vision.maxReprojectionErrorPx,
      });

    this.smoother = new PoseSmoother({
      windowSeconds: policy.smoothing.windowSeconds,
      minSamples: policy.smoothing.minSamples,
      maxSampleAgeSeconds: policy.smoothing.maxSampleAgeSeconds,
      stabilityReferenceDegrees: policy.smoothing.stabilityReferenceDegrees,
    });

    const thresholds = this.resolveThresholds(options.calibration ?? null);
    this.classifier = new DirectionClassifier(policy.thresholds, thresholds);

    this.engine = new AttentionStateEngine(policy.events, {
      minPoseConfidence: policy.vision.minPoseConfidence,
      collectedSignals: policy.privacy.collectSignals,
    });

    this.sessionId = options.sessionId ?? policy.runtime.sessionId ?? '';
    this.detector = new AttentionEventDetector(policy.events, {
      sessionId: this.sessionId,
      agentVersion: options.agentVersion ?? '',
      modelVersion: this.estimator.modelVersion,
      configVersion: '',
    });

    // A single sample may not be credited more than a few multiples of the inference
    // interval, so a gap in monitoring is reported as unmonitored rather than silently
    // attributed to whichever state happened to be current.
    this.stateStats = new StateAccumulator(
      Math.max(2, (1 / Math.max(policy.vision.minProcessFps, 0.1)) * 3),
    );
  }

  /** Stamp the resolved policy fingerprint onto emitted events. */
  setConfigVersion(configVersion: string): void {
    // The detector owns event construction, so version stamping lives there.
    this.detector.setConfigVersion(configVersion);
  }

  get effectiveThresholds(): EffectiveThresholds {
    return this.classifier.effectiveThresholds;
  }

  get modelVersion(): string {
    return this.estimator.modelVersion;
  }

  /** Apply (or replace) a calibration profile without restarting the session. */
  applyCalibration(profile: CalibrationProfile | null): void {
    this.classifier.applyThresholds(this.resolveThresholds(profile));
  }

  private resolveThresholds(profile: CalibrationProfile | null): EffectiveThresholds {
    if (profile === null) {
      if (this.policy.calibration.enabled) {
        this.addNote(
          'no calibration profile for this workstation; organisation-wide thresholds are in ' +
            'use, which is less accurate when the camera is not centred on the work screen',
        );
      }
      return thresholdsFromPolicy(this.policy.thresholds);
    }

    const staleness = profileStalenessNote(
      profile,
      this.policy.calibration.maxProfileAgeDays,
      this.clock.nowUtc(),
    );
    if (staleness !== null) this.addNote(staleness);
    for (const note of profile.notes) this.addNote(note);

    return effectiveThresholdsFor(profile, this.policy.thresholds);
  }

  private addNote(note: string): void {
    if (!this.notes.includes(note)) this.notes.push(note);
  }

  process(input: FrameInput): PipelineTick {
    this.framesProcessed += 1;
    if (this.firstTs === null) this.firstTs = input.monotonicTs;
    this.lastTs = input.monotonicTs;

    // --- stage 2/3: quality and landmarks -----------------------------------------
    let quality: FrameQuality =
      input.metrics === null
        ? unavailableQuality('camera_unavailable')
        : assessFrame(input.metrics, this.policy.quality);

    if (input.metrics !== null) {
      quality = assessFace(
        quality,
        input.landmarks,
        this.policy.quality,
        this.policy.vision.minLandmarkConfidence,
      );
    }

    // --- stage 4: head pose --------------------------------------------------------
    let poseConfidence = 0;
    let reprojectionErrorPx: number | null = null;
    let rawPose = null as FaceObservation['headPose'];

    if (input.landmarks !== null) {
      this.framesWithFace += 1;
      const estimate = this.estimator.estimate(
        input.landmarks,
        this.policy.vision.presenceConfidence,
      );
      if (estimate === null) {
        this.framesUnsolved += 1;
      } else {
        rawPose = estimate.pose;
        poseConfidence = estimate.confidence;
        reprojectionErrorPx = estimate.reprojectionErrorPx;
        if (estimate.reprojectionErrorPx > this.policy.vision.maxReprojectionErrorPx) {
          quality = withFlag(quality, 'high_reprojection_error');
        }
      }
    }

    const observation: FaceObservation = {
      timestampUtc: input.timestampUtc,
      monotonicTs: input.monotonicTs,
      faceDetected: input.landmarks !== null,
      faceCount: input.landmarks?.faceCount ?? 0,
      quality,
      headPose: rawPose,
      detectionConfidence: input.landmarks === null ? 0 : this.policy.vision.presenceConfidence,
      poseConfidence,
      reprojectionErrorPx,
    };

    // --- stage 5: smoothing --------------------------------------------------------
    const qualityFactor = qualityConfidenceFactor(quality);
    const poseUsable =
      rawPose !== null && qualityFactor > 0 && poseConfidence > 0;

    if (poseUsable && rawPose !== null) {
      this.smoother.add(input.monotonicTs, rawPose, poseConfidence * qualityFactor);
    } else {
      // A frame that cannot contribute must actively expire stale samples, otherwise a face
      // that has left the frame keeps contributing a confident "facing forward" reading.
      this.smoother.markGap(input.monotonicTs);
    }
    const smoothed = this.smoother.smoothed(input.monotonicTs);

    // --- stage 6: direction --------------------------------------------------------
    const decision = this.classifier.classify(smoothed, quality);
    const sampleConfidence =
      smoothed === null
        ? 0
        : combineConfidence(
            smoothed.effectiveConfidence,
            qualityFactor,
            decision.confidenceFactor,
          );

    const sample: DirectionSample = {
      monotonicTs: input.monotonicTs,
      timestampUtc: input.timestampUtc,
      direction: decision.direction,
      confidence: sampleConfidence,
      smoothedPose: smoothed?.pose ?? null,
      rawPose,
      reasons: decision.reasons,
      quality,
    };

    // --- stages 7-8: state and events ---------------------------------------------
    const evaluation = this.engine.observe(sample, null);
    const events = this.detector.observe(sample);

    this.stateStats.observe(evaluation.inference.state, input.monotonicTs);
    this.eventStats.extend(events);
    this.eventStats.noteMinorShifts(this.detector.minorShiftCount);
    this.eventStats.setObservedWindow(this.observedWindowSeconds);

    return { observation, sample, evaluation, events };
  }

  /** Close any in-progress episode. Call once at session end. */
  flush(): AttentionEvent[] {
    const events = this.detector.flush();
    this.eventStats.extend(events);
    this.eventStats.noteMinorShifts(this.detector.minorShiftCount);
    this.eventStats.setObservedWindow(this.observedWindowSeconds);
    return events;
  }

  get observedWindowSeconds(): number {
    if (this.firstTs === null || this.lastTs === null) return 0;
    return Math.max(0, this.lastTs - this.firstTs);
  }

  summary(): PipelineSummary {
    return {
      sessionId: this.sessionId,
      configVersion: this.detector.stampedConfigVersion,
      modelVersion: this.estimator.modelVersion,
      eventStats: this.eventStats.snapshot(),
      minorShiftCount: this.detector.minorShiftCount,
      ignoredShiftCount: this.detector.ignoredCount,
      suppressedEventCount: this.detector.suppressedEventCount,
      stateTotals: this.stateStats.toJson(),
      framesProcessed: this.framesProcessed,
      framesWithFace: this.framesWithFace,
      framesUnsolved: this.framesUnsolved,
      notes: [...this.notes],
    };
  }

  get stateAccumulator(): StateAccumulator {
    return this.stateStats;
  }

  get eventAccumulator(): EventAccumulator {
    return this.eventStats;
  }
}

/**
 * Adaptive inference-rate controller (§26, §27).
 *
 * Lowers the inference rate when frames start costing more than the configured budget, and
 * raises it back when there is headroom. The floor is not merely a performance setting: the
 * config validator refuses a floor too low to fill the smoothing window, because a starved
 * pipeline reports `unknown` rather than degrading quietly — and "the agent stopped being
 * able to tell" is a materially different statement from "the employee looked away".
 */
export class AdaptiveRateController {
  private readonly minFps: number;
  private readonly maxFps: number;
  private readonly budgetMs: number;
  private readonly enabled: boolean;
  private currentFps: number;
  private recent: number[] = [];

  constructor(vision: Pick<Policy['vision'], 'minProcessFps' | 'maxProcessFps' | 'targetProcessFps' | 'frameBudgetMs' | 'adaptiveFps'>) {
    this.minFps = vision.minProcessFps;
    this.maxFps = vision.maxProcessFps;
    this.budgetMs = vision.frameBudgetMs;
    this.enabled = vision.adaptiveFps;
    this.currentFps = vision.targetProcessFps;
  }

  get fps(): number {
    return this.currentFps;
  }

  get intervalMs(): number {
    return 1000 / this.currentFps;
  }

  /** Record how long one inference took, and adjust the rate. */
  record(latencyMs: number): number {
    this.recent.push(latencyMs);
    if (this.recent.length > 15) this.recent.shift();
    if (!this.enabled || this.recent.length < 5) return this.currentFps;

    const mean = this.recent.reduce((sum, value) => sum + value, 0) / this.recent.length;
    if (mean > this.budgetMs) {
      this.currentFps = Math.max(this.minFps, this.currentFps * 0.8);
      this.recent = [];
    } else if (mean < this.budgetMs * 0.5) {
      this.currentFps = Math.min(this.maxFps, this.currentFps * 1.1);
      this.recent = [];
    }
    return this.currentFps;
  }

  get meanLatencyMs(): number | null {
    if (this.recent.length === 0) return null;
    return this.recent.reduce((sum, value) => sum + value, 0) / this.recent.length;
  }
}
